import React, { useEffect, useState, useMemo } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useGlobalLoader } from "@/context/GlobalLoaderContext";
import { startOfToday } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  FileText,
  Eye,
  EyeOff,
  WifiSync,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  EyeIcon,
  CheckCircle,
  Download,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, set } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { form } from "framer-motion/client";

export default function AdminGeneratedSOA() {
  const [clients, setClients] = useState([]);
  const [servers, setServers] = useState([]);
  const [napBoxes, setNapBoxes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [availablePorts, setAvailablePorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showLoader, hideLoader, showSuccess } = useGlobalLoader();
  const [open, setOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("active");
  const [serverFilter, setServerFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editablePrice, setEditablePrice] = useState("");
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPPPoEPassword, setShowPPPoEPassword] = useState(false);
  const [generatingSOA, setGeneratingSOA] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [billingDetails, setBillingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [firstbillNoOfDays, setFirstbillNoOfDays] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  const [reconnectDialog, setReconnectDialog] = useState(false);
  const [reconnectData, setReconnectData] = useState({
    notification: "",
    title: "",
    message: "",
  });

  // Pagination
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    client_name: "",
    email: "",
    contact_number: "",
    address: "",
    plan_name: "",
    monthly_fee: "",
    due_date: "",
    assigned_server: "",
    nap_box_id: "none",
    port_number: "",
    modem_serial_number: "",
    status: "New Application",
    cut_off_day: "1",
    due_date_day: "15",
    username: "",
    password: "",
    pppoe_username: "",
    pppoe_password: "",
    grace_period_days: "3",
    due_days_allowance: "5",
    account_status: "active",
    auto_disconnected: "auto disconnect",
    is_resend_welcome_email: false,
    installation_date: "",
    first_bill_no_of_days: "0",
  });

  // Date formatting helper - MMM-dd-yyyy format (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const getOrdinal = (day) => {
    if (day >= 11 && day <= 13) return day + "th";

    switch (day % 10) {
      case 1:
        return day + "st";
      case 2:
        return day + "nd";
      case 3:
        return day + "rd";
      default:
        return day + "th";
    }
  };

  const formatDateWithOrdinal = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");

    const day = getOrdinal(date.getDate());

    return `${date.toLocaleString("en-US", { month: "long" })} ${day}, ${date.getFullYear()}`;
  };

  const formatDateYMD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchMetaData(); // servers, nap boxes, plans
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(delay);
  }, [searchQuery]);

  useEffect(() => {
    fetchData(1, debouncedSearch); // reset to page 1 when filters change
  }, [accountStatusFilter, serverFilter, balanceFilter, debouncedSearch]);

  const fetchData = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API}/generated_soa`, {
        params: {
          page,
          size: itemsPerPage,
          search,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setClients(res.data.items || []);
      setTotalClients(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
      setCurrentPage(res.data.page || page);
    } catch (err) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetaData = async () => {
    try {
      const token = localStorage.getItem("token");

      const [serversRes, napBoxesRes, plansRes] = await Promise.all([
        axios.get(`${API}/servers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/nap-boxes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/plans`),
      ]);

      setServers(serversRes.data || []);
      setNapBoxes(napBoxesRes.data || []);
      setPlans(plansRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch metadata");
    }
  };

  const fetchAvailablePorts = async (napBoxId) => {
    if (!napBoxId || napBoxId === "none") {
      setAvailablePorts([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/nap-boxes/${napBoxId}/available-ports`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAvailablePorts(res.data);
    } catch (error) {
      console.error("Failed to fetch available ports");
      setAvailablePorts([]);
    }
  };

  const handleNapBoxChange = (napBoxId) => {
    setFormData({ ...formData, nap_box_id: napBoxId, port_number: "" });
    fetchAvailablePorts(napBoxId);
  };

  const [cutOffDueDate, setCutOffDueDate] = useState({
    client: null,
    cut_off_day: "",
    due_date_day: "",
    installation_date: "",
  });

  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day); // LOCAL time
  };

  const getDateDiff = (startDateStr, endDateStr) => {
    const msPerDay = 1000 * 60 * 60 * 24;

    const startDate = parseLocalDate(startDateStr);
    const endDate = parseLocalDate(endDateStr);

    const diff = endDate - startDate;

    return Math.round(diff / msPerDay); // safer than ceil
  };

  const computeDates = (installationDate, grace) => {
    if (!installationDate) {
      return {
        cut_off_day: "",
        due_date_day: "",
      };
    }

    const baseDate = new Date(installationDate);
    const cutOffDay = baseDate.getDate();

    // clone date (important)
    const dueDate = new Date(baseDate);

    // let JS handle overflow (THIS is the key)
    dueDate.setDate(baseDate.getDate() + parseInt(grace));
    console.log(dueDate);

    console.log(baseDate);
    console.log(cutOffDay);

    const cutoffDate = new Date(baseDate);
    cutoffDate.setDate(cutOffDay);
    console.log(cutoffDate);

    return {
      cut_off_day: String(cutOffDay),
      due_date_day: String(dueDate.getDate()),
      due_date: formatDate(dueDate),
    };
  };

  const handleRefresh = () => {
    fetchData(currentPage, debouncedSearch);
  };

  const handleInstallationDateChange = (date) => {
    const result = computeDates(date, parseInt(formData.due_days_allowance));

    setFormData((prev) => ({
      ...prev,
      installation_date: date,
      ...result,
    }));
  };

  const handleDueDayAllowanceChange = (val) => {
    const result = computeDates(formData.installation_date, parseInt(val));

    setFormData((prev) => ({
      ...prev,
      due_days_allowance: val,
      ...result,
    }));
  };

  const handleCutOffDayChange = (val) => {
    const result = computeDatesFromCutOff(
      formData.installation_date,
      parseInt(val),
      parseInt(formData.due_days_allowance),
    );

    setFormData((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const handleCutOffDayLoad = (val, installation_date, due_days_allowance) => {
    const result = computeDatesFromCutOff(
      installation_date,
      parseInt(val),
      parseInt(due_days_allowance),
    );

    setFormData((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const computeDatesFromCutOff = (installationDate, cutOffDay, grace) => {
    if (!installationDate) {
      return {
        cut_off_day: "",
        due_date_day: "",
      };
    }

    const baseDate = new Date(installationDate);
    const installDay = baseDate.getDate();

    // Step 1: set cut-off in current month
    let cutOffDate = new Date(baseDate);
    cutOffDate.setDate(cutOffDay);

    // Step 2: if cut-off < installation day → move to next month
    if (cutOffDay < installDay) {
      cutOffDate.setMonth(cutOffDate.getMonth() + 1);
    }

    console.log("Installation Day", installDay);
    console.log("Cut-Off Day", cutOffDay);

    // Step 3: compute due date
    const dueDate = new Date(cutOffDate);
    dueDate.setDate(cutOffDate.getDate() + parseInt(grace));
    // console.log(dueDate);

    // const cutoffDate = new Date(baseDate);
    // cutOffDate.setDate(baseDate.getDate() + cutOffDate.getDate());
    // console.log(cutoffDate);

    let dateDiff = getDateDiff(
      formatDateYMD(baseDate),
      formatDateYMD(cutOffDate),
    );
    console.log("Installation:", formatDateYMD(baseDate));
    console.log("Cut-Off:", formatDateYMD(cutOffDate));
    console.log("Date Diff", dateDiff);
    // console.log(formatDateYMD(cutOffDate));

    setFirstbillNoOfDays(dateDiff);

    return {
      cut_off_day: String(cutOffDay),
      due_date_day: String(dueDate.getDate()),
      // optional (recommended)
      due_date: formatDate(dueDate),
      first_bill_no_of_days: dateDiff,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader(editingClient ? "Updating client..." : "Creating client...");

    try {
      const token = localStorage.getItem("token");
      const data = {
        ...formData,
        monthly_fee: parseFloat(Number(editablePrice).toFixed(2)) || 0,
        nap_box_id: formData.nap_box_id === "none" ? null : formData.nap_box_id,
        port_number: formData.port_number
          ? parseInt(formData.port_number)
          : null,
        cut_off_day: parseInt(formData.cut_off_day) || 1,
        due_date_day: parseInt(formData.due_date_day) || 15,
        grace_period_days: parseInt(formData.grace_period_days) || 3,
        due_days_allowance: parseInt(formData.due_days_allowance) || 5,
        account_status: formData.account_status || "active",
        auto_disconnected: formData.auto_disconnected || "auto disconnect",
        status: formData.status || "New Application",
        is_resend_welcome_email: formData.is_resend_welcome_email || false,
        first_bill_no_of_days: parseInt(firstbillNoOfDays) || 0,
      };

      if (editingClient && !data.password) {
        delete data.password;
      }

      if (!data.pppoe_username) {
        data.pppoe_username = data.pppoe_username;
      }

      if (!data.pppoe_password) {
        data.pppoe_password = data.pppoe_password;
      }

      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(
          `Client created! Username: ${data.username || "auto-generated"}, Password: ${data.password || "client123"}`,
        );
      }

      fetchData(currentPage, debouncedSearch);
      showSuccess("Client updated successfully!");
      setOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
      // } finally {
      //   showSuccess("Client updated successfully!");
      //   // hideLoader();
    }
  };

  // const handleGenerateSOA = async (clientId) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     await axios.post(
  //       `${API}/soa/generate/${clientId}`,
  //       {},
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       },
  //     );
  //     toast.success("SOA generated successfully!");
  //     fetchData(currentPage, debouncedSearch);
  //   } catch (error) {
  //     toast.error(error.response?.data?.detail || "Failed to generate SOA");
  //   }
  // };

  const viewBillingDetails = async (clientId) => {
    setLoadingDetails(true);
    setViewDialog(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/payments/billing-details/${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setBillingDetails(response.data);
    } catch (error) {
      toast.error("Failed to fetch billing details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleGenerateSOAForAll = async () => {
    if (
      !window.confirm(
        "Generate SOA for all clients? This will update their balance and status.",
      )
    )
      return;
    setGeneratingSOA(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/soa/generate-all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(`SOA generated for ${res.data.generated} clients!`);
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate SOAs");
    } finally {
      setGeneratingSOA(false);
    }
  };

  const handlePlanSelect = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      const result = computeDatesFromCutOff(
        formData.installation_date,
        parseInt(formData.cut_off_day),
        parseInt(formData.due_days_allowance),
      );

      setSelectedPlan(plan);
      setEditablePrice(plan.price);
      setIsEditingPrice(false);
      setFormData({
        ...formData,
        plan_name: plan.name,
        monthly_fee: plan.price.toString(),
        ...result,
      });
    }
  };

  const handlePriceEdit = () => {
    setIsEditingPrice(true);
  };

  // Dialog state
  const [dialog, setDialog] = React.useState({
    show: false,
    action: "", // "reconnect", "delete", etc.
    client: null,
  });

  // Open dialog for a row
  const openDialog = (client, action) => {
    setDialog({
      show: true,
      action,
      client,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Client deleted successfully");
      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  // Handle confirm
  const handleConfirm = async () => {
    if (!dialog.client) return;

    try {
      const token = localStorage.getItem("token");

      if (dialog.action === "reconnect") {
        showLoader("Reconnecting client...");
        await axios.post(
          `${API}/mikrotik/unblock-client/${dialog.client.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        showSuccess("Client reconnected successfully");
        // hideLoader();
      }

      if (dialog.action === "delete") {
        showLoader("Deleting SOA client...");
        await axios.delete(`${API}/generated_soa/${dialog.client.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        hideLoader();
        showSuccess("Client SOA deleted successfully");
      }

      if (dialog.action === "generate soa") {
        showLoader("Generating SOA...");
        await axios.post(
          `${API}/soa/generate/${dialog.client.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        hideLoader();
        showSuccess("SOA generated successfully");
      }

      fetchData(currentPage, debouncedSearch);
    } catch (error) {
      hideLoader();
      console.error(error);
      toast.error(error.response?.data?.detail || "Action failed");
    }

    setDialog({ show: false, action: "", client: null });
  };

  const openEditDialog = async (client) => {
    setEditingClient(client);

    // Find matching plan
    const matchingPlan = plans.find((p) => p.name === client.plan_name);
    setSelectedPlan(matchingPlan || null);
    setEditablePrice(client.monthly_fee ? client.monthly_fee.toString() : "");
    setIsEditingPrice(false);

    // Fetch available ports for this NAP box (including current client's port)
    if (client.nap_box_id) {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API}/nap-boxes/${client.nap_box_id}/available-ports`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        let ports = res.data || [];
        // Add current client's port to available ports if not already there
        if (
          client.port_number &&
          !ports.find((p) => p.port_number === client.port_number)
        ) {
          ports = [
            ...ports,
            { port_number: client.port_number, status: "Current" },
          ];
        }
        setAvailablePorts(ports);
      } catch (error) {
        console.error("Failed to fetch available ports");
        // Still add current port if client has one
        if (client.port_number) {
          setAvailablePorts([
            { port_number: client.port_number, status: "Current" },
          ]);
        } else {
          setAvailablePorts([]);
        }
      }
    } else {
      setAvailablePorts([]);
    }

    setCutOffDueDate({
      ...cutOffDueDate,
      installation_date: client.installation_date
        ? format(client.installation_date, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
    });

    setFirstbillNoOfDays(
      client.first_bill_no_of_days
        ? client.first_bill_no_of_days.toString()
        : "0",
    );

    setFormData({
      client_name: client.client_name || "",
      email: client.email || "",
      contact_number: client.contact_number || "",
      address: client.address || "",
      plan_name: client.plan_name || "",
      monthly_fee: client.monthly_fee ? client.monthly_fee.toString() : "",
      due_date: client.due_date || "",
      assigned_server: client.assigned_server || "",
      nap_box_id: client.nap_box_id || "none",
      port_number: client.port_number ? client.port_number.toString() : "",
      modem_serial_number: client.modem_serial_number || "",
      status: "New",
      cut_off_day: (client.cut_off_day || 1).toString(),
      due_date_day: (client.due_date_day || 15).toString(),
      username: client.username || "",
      password: "", // Don't show existing password
      pppoe_username: client.pppoe_username || client.username || "",
      pppoe_password: client.pppoe_password || client.pppoe_username || "", // Don't show existing password
      grace_period_days: (client.grace_period_days || 3).toString(),
      due_days_allowance: (client.due_days_allowance || 5).toString(),
      account_status: client.account_status || "active",
      auto_disconnected: client.auto_disconnected || "auto disconnect",
      is_resend_welcome_email: true,
      installation_date:
        client.installation_date || format(new Date(), "yyyy-MM-dd"),
      first_bill_no_of_days: client.first_bill_no_of_days || "0",
    });

    console.log("Installation:", client.cut_off_day);
    console.log("Cut-Off:", client);
    console.log("Date Diff", client.due_days_allowance);

    handleCutOffDayLoad(
      client.cut_off_day,
      client.installation_date,
      client.due_days_allowance,
    );

    setShowPassword(false);
    setOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setSelectedPlan(null);
    setShowPassword(false);
    setAvailablePorts([]);
    setFirstbillNoOfDays("0");
    setFormData({
      client_name: "",
      email: "",
      contact_number: "",
      address: "",
      plan_name: "",
      monthly_fee: "",
      due_date: "",
      assigned_server: "",
      nap_box_id: "none",
      port_number: "",
      modem_serial_number: "",
      status: "New",
      cut_off_day: "1",
      due_date_day: "15",
      username: "",
      password: "",
      pppoe_username: "",
      pppoe_password: "",
      grace_period_days: "3",
      due_days_allowance: "5",
      account_status: "active",
      auto_disconnected: "auto disconnect",
      is_resend_welcome_email: true,
      installation_date: format(new Date(), "yyyy-MM-dd"),
      first_bill_no_of_days: "0",
    });
  };

  const openReconnectDialog = (reconnect) => {
    // const paymentCheck = canMakePayment(client);
    // if (!paymentCheck.canPay) {
    //   toast.error(paymentCheck.reason);
    //   return;
    // }

    // // Prevent overpayment - set max amount to current balance
    // const maxAmount = client.balance || client.monthly_fee || 0;
    setReconnectData({
      reconnect_id: reconnect.id,
      title: reconnect.title,
      message: reconnect.message,
    });
    // markAsRead(reconnect.id)};
    setReconnectDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      Paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Unpaid: "bg-red-100 text-red-700 border-red-200",
      Disconnected: "bg-slate-100 text-slate-700 border-slate-200",
      Extended: "bg-blue-100 text-blue-700 border-blue-200",
      New: "bg-green-100 text-green-700 border-green-200",
    };
    return (
      <span
        className={`px-2 sm:px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Paid}`}
      >
        {status}
      </span>
    );
  };

  // Download SOA PDF
  const downloadSOAPDF = async (soaId, soaNumber) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/soa/${soaId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SOA_${soaNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const capitalizeFirst = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const getAccountStatusBadge = (status) => {
    const colors = {
      active: "bg-emerald-100 text-emerald-700 border-emerald-300",
      inactive: "bg-red-100 text-red-700 border-red-300",
    };
    return (
      <span
        className={`px-2 sm:px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.active}`}
      >
        {capitalizeFirst(status)}
      </span>
    );
  };
  const getNapBoxName = (napBoxId) => {
    const box = napBoxes.find((b) => b.id === napBoxId);
    return box ? box.name : "-";
  };

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
        {/* <div className="p-4 sm:p-6 lg:p-8"> */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1"
              data-testid="new-applications-title"
            >
              Generated SOA's
            </h1>
            <p className="text-sm text-slate-600">
              Manage your client statement of account here.
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3 flex-wrap">
            <Dialog
              open={open}
              onOpenChange={(val) => {
                setOpen(val);
                if (!val) resetForm();
              }}
            >
              <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
                {loading && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700 font-semibold">
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      Saving client...
                    </div>
                  </div>
                )}

                <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                  <DialogTitle className="text-xl font-bold">
                    {editingClient ? "Update New Client" : "Add New Client"}
                  </DialogTitle>
                </DialogHeader>

                <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="client-form"
                  >
                    <div className="relative">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Account Status</Label>
                          <Select
                            value={formData.account_status}
                            onValueChange={(val) =>
                              setFormData({ ...formData, account_status: val })
                            }
                          >
                            <SelectTrigger data-testid="client-account_status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              {/* <SelectItem value="inactive">Inactive</SelectItem> */}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Disconnection</Label>
                          <Select
                            value={formData.auto_disconnected}
                            onValueChange={(val) =>
                              setFormData({
                                ...formData,
                                auto_disconnected: val,
                              })
                            }
                          >
                            <SelectTrigger data-testid="client-auto_disconnected-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto disconnect">
                                Auto Disconnect
                              </SelectItem>
                              <SelectItem value="skip disconnect">
                                Skip Disconnect
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="mt-2">
                          <Label>Payment Status</Label>
                          <Select
                            value={formData.status}
                            onValueChange={(val) =>
                              setFormData({ ...formData, status: val })
                            }
                          >
                            <SelectTrigger data-testid="client-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {/* <SelectItem value="Paid">Paid</SelectItem>
                              <SelectItem value="Unpaid">Unpaid</SelectItem>
                              <SelectItem value="Disconnected">
                                Disconnected
                              </SelectItem>
                              <SelectItem value="Free">Free</SelectItem> */}
                              <SelectItem value="New">New</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Checkbox for sending email if the status is New */}
                        <div className=" items-center space-x-2 mb-2">
                          <input
                            type="checkbox"
                            checked={formData.is_resend_welcome_email || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                is_resend_welcome_email: e.target.checked,
                              })
                            }
                          />
                          <Label htmlFor="send-email">Send welcome email</Label>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <Label>Full Name</Label>
                          <Input
                            data-testid="client-name-input"
                            value={formData.client_name}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                client_name: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            data-testid="client-email-input"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                email: e.target.value,
                                username: e.target.value,
                              })
                            }
                            required
                            // disabled={!!editingClient}
                          />
                        </div>
                        <div>
                          <Label>Contact Number</Label>
                          <Input
                            data-testid="client-contact-input"
                            value={formData.contact_number}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                contact_number: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          data-testid="client-address-input"
                          value={formData.address}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              address: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Installation Date</Label>

                          <Popover open={dateOpen} onOpenChange={setDateOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                disabled={formData.status !== "New"}
                                variant="outline"
                                className="w-full justify-between text-left text-[15px] font-semibold h-10 mt-2"
                              >
                                {cutOffDueDate.installation_date
                                  ? format(
                                      new Date(cutOffDueDate.installation_date),
                                      "PPP",
                                    )
                                  : "Select Installation date"}

                                <CalendarIcon className="w-4 h-6 text-slate-500" />
                              </Button>
                            </PopoverTrigger>

                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={
                                  cutOffDueDate.installation_date
                                    ? new Date(cutOffDueDate.installation_date)
                                    : null
                                }
                                onSelect={(date) => {
                                  if (!date) return; // ✅ prevent error

                                  setCutOffDueDate({
                                    ...cutOffDueDate,
                                    installation_date: format(
                                      date,
                                      "yyyy-MM-dd",
                                    ),
                                  });

                                  handleInstallationDateChange(
                                    format(date, "yyyy-MM-dd"),
                                  );

                                  setDateOpen(false); // 👈 close picker
                                }}
                                disabled={(date) => date < startOfToday()} // prevent past dates
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label>Due Date</Label>
                          <Button
                            disabled
                            variant="outline"
                            className="w-full justify-between text-left text-[15px] font-semibold h-10 mt-2"
                          >
                            {formData.due_date
                              ? format(new Date(formData.due_date), "PPP")
                              : "Due date"}

                            <CalendarIcon className="w-4 h-6 text-slate-500" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Select Plan
                        </Label>
                        <Select
                          value={selectedPlan?.id || "select-plan"}
                          onValueChange={(val) =>
                            val !== "select-plan" && handlePlanSelect(val)
                          }
                        >
                          <SelectTrigger
                            data-testid="client-plan-select"
                            className="mt-1.5 rounded-lg"
                          >
                            <SelectValue placeholder="Choose a plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {plans?.length === 0 ? (
                              <SelectItem value="no-plans" disabled>
                                No plans available
                              </SelectItem>
                            ) : (
                              plans.map((plan) => (
                                <SelectItem key={plan.id} value={plan.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span className="font-medium">
                                      {plan.name}
                                    </span>
                                    <span className="text-slate-600 ml-4">
                                      ₱{plan.price}/mo
                                    </span>
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        {selectedPlan && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-900">
                                {selectedPlan.name}
                              </span>
                              {isEditingPrice ? (
                                <input
                                  type="number"
                                  value={editablePrice}
                                  onChange={(e) =>
                                    setEditablePrice(e.target.value)
                                  }
                                  className="border rounded px-2 py-1 w-28"
                                />
                              ) : (
                                <span className="text-lg font-bold text-blue-700">
                                  ₱
                                  {new Intl.NumberFormat("en-PH").format(
                                    editablePrice,
                                  )}
                                  /mo
                                </span>
                              )}
                            </div>
                            <div className="flex gap-2 justify-end">
                              {!isEditingPrice && (
                                <Button onClick={handlePriceEdit}>
                                  Edit Price
                                </Button>
                              )}

                              {isEditingPrice && (
                                <Button
                                  onClick={() => setIsEditingPrice(false)}
                                >
                                  Save
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {firstbillNoOfDays > 0 && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-red-900">
                              FIRST BILL
                            </span>
                            <span className="text-lg font-bold text-red-700">
                              ₱
                              {new Intl.NumberFormat("en-PH", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(
                                (firstbillNoOfDays / 30) * editablePrice,
                              )}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-slate-600">
                            Number of Days: {firstbillNoOfDays}
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Cut-off Day</Label>
                          <Select
                            disabled={formData.status !== "New"}
                            value={formData.cut_off_day}
                            onValueChange={handleCutOffDayChange}
                          >
                            <SelectTrigger data-testid="client-cutoff-select">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 30 }, (_, i) => i + 1).map(
                                (day) => (
                                  <SelectItem key={day} value={day.toString()}>
                                    {day}
                                    {day === 1
                                      ? "st"
                                      : day === 2
                                        ? "nd"
                                        : day === 3
                                          ? "rd"
                                          : "th"}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Due Day Allowance</Label>
                          <Select
                            disabled={formData.status !== "New"}
                            value={formData.due_days_allowance}
                            onValueChange={handleDueDayAllowanceChange}
                          >
                            <SelectTrigger data-testid="client-dueday-allowance-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 day</SelectItem>
                              <SelectItem value="2">2 days</SelectItem>
                              <SelectItem value="3">3 days</SelectItem>
                              <SelectItem value="5">5 days</SelectItem>
                              <SelectItem value="7">7 days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* MikroTik Settings */}
                      <div className="border-t pt-4 mt-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Due Date Day</Label>
                            <Select
                              disabled
                              value={formData.due_date_day}
                              onValueChange={(val) =>
                                setFormData({ ...formData, due_date_day: val })
                              }
                            >
                              <SelectTrigger data-testid="client-dueday-select">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from(
                                  { length: 30 },
                                  (_, i) => i + 1,
                                ).map((day) => (
                                  <SelectItem key={day} value={day.toString()}>
                                    {day}
                                    {day === 1
                                      ? "st"
                                      : day === 2
                                        ? "nd"
                                        : day === 3
                                          ? "rd"
                                          : "th"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Grace Period</Label>
                            <Select
                              value={formData.grace_period_days}
                              onValueChange={(val) =>
                                setFormData({
                                  ...formData,
                                  grace_period_days: val,
                                })
                              }
                              // onValueChange={handleGracePeriodChange}
                            >
                              <SelectTrigger data-testid="client-grace-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1 day</SelectItem>
                                <SelectItem value="2">2 days</SelectItem>
                                <SelectItem value="3">3 days</SelectItem>
                                <SelectItem value="5">5 days</SelectItem>
                                <SelectItem value="7">7 days</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      {/* NAP Box and Port Selection */}
                      <div className="border-t pt-4 mt-2">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          Connection Details
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Server</Label>
                            <Select
                              value={
                                formData.assigned_server || "select-server"
                              }
                              onValueChange={(val) =>
                                val !== "select-server" &&
                                setFormData({
                                  ...formData,
                                  assigned_server: val,
                                })
                              }
                            >
                              <SelectTrigger data-testid="client-server-select">
                                <SelectValue placeholder="Select server" />
                              </SelectTrigger>
                              <SelectContent>
                                {servers?.length === 0 ? (
                                  <SelectItem value="no-servers" disabled>
                                    No servers available
                                  </SelectItem>
                                ) : (
                                  servers.map((server) => (
                                    <SelectItem
                                      key={server.id}
                                      value={server.id}
                                    >
                                      {server.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>NAP Box</Label>
                            <Select
                              value={formData.nap_box_id || "none"}
                              onValueChange={handleNapBoxChange}
                            >
                              <SelectTrigger data-testid="client-napbox-select">
                                <SelectValue placeholder="Select NAP Box" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {napBoxes.map((box) => (
                                  <SelectItem
                                    className="py-3"
                                    key={box.id}
                                    value={box.id}
                                  >
                                    {box.name} ({box.available_ports || 0}{" "}
                                    available)
                                    <br />
                                    <span className="text-xs text-blue-500">
                                      {box.location}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {formData.nap_box_id &&
                            formData.nap_box_id !== "none" && (
                              <div>
                                <Label>Port Number</Label>
                                <Select
                                  value={formData.port_number || "select-port"}
                                  onValueChange={(val) =>
                                    val !== "select-port" &&
                                    setFormData({
                                      ...formData,
                                      port_number: val,
                                    })
                                  }
                                >
                                  <SelectTrigger data-testid="client-port-select">
                                    <SelectValue placeholder="Select port" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePorts?.length === 0 ? (
                                      <SelectItem
                                        value="no-ports-available"
                                        disabled
                                      >
                                        No available ports
                                      </SelectItem>
                                    ) : (
                                      availablePorts.map((port) => (
                                        <SelectItem
                                          key={port.port_number}
                                          value={port.port_number.toString()}
                                        >
                                          Port #{port.port_number}{" "}
                                          {port.status === "Current"
                                            ? "(Current)"
                                            : ""}
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                        </div>
                      </div>
                      {/* Client Portal Credentials */}
                      <div className="border-t pt-4 mt-2">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          Client Portal Credentials
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Username</Label>
                            <Input
                              data-testid="client-username-input"
                              value={formData.username}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  username: e.target.value,
                                })
                              }
                              placeholder={
                                editingClient
                                  ? "Leave empty to keep"
                                  : "Auto-generated"
                              }
                            />
                          </div>
                          <div>
                            <Label>Password</Label>
                            <div className="relative">
                              <Input
                                type={showPassword ? "text" : "password"}
                                data-testid="client-password-input"
                                value={formData.password}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    password: e.target.value,
                                  })
                                }
                                placeholder={
                                  editingClient
                                    ? "Leave empty to keep"
                                    : "Default: client123"
                                }
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="w-8 h-8" />
                                ) : (
                                  <Eye className="w-8 h-8" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* MikroTik Settings */}
                      <div className="border-t pt-4 mt-2">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          MikroTik Settings
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label>PPPoE Username</Label>
                            <Input
                              data-testid="client-pppoe-input"
                              value={formData.pppoe_username}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  pppoe_username: e.target.value,
                                })
                              }
                              placeholder="Same as username if empty"
                            />
                          </div>

                          <div>
                            <Label>PPPoE Password</Label>
                            <div className="relative">
                              <Input
                                type={showPPPoEPassword ? "text" : "password"}
                                data-testid="client-pppoe-password-input"
                                value={formData.pppoe_password}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    pppoe_password: e.target.value,
                                  })
                                }
                                placeholder={
                                  editingClient
                                    ? "Keep PPPoE Username"
                                    : "Default: PPPoE Username"
                                }
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                                onClick={() =>
                                  setShowPPPoEPassword(!showPPPoEPassword)
                                }
                              >
                                {showPPPoEPassword ? (
                                  <EyeOff className="w-8 h-8" />
                                ) : (
                                  <Eye className="w-8 h-8" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Modem Serial Number</Label>
                            <Input
                              data-testid="client-modem-input"
                              value={formData.modem_serial_number}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  modem_serial_number: e.target.value,
                                })
                              }
                              placeholder="e.g., SN123456789"
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg disabled:bg-gray-400  text-[20px] font-semibold h-14 mt-4"
                      >
                        {loading
                          ? "Saving..."
                          : editingClient
                            ? "Update Client"
                            : "Create Client"}
                      </Button>
                    </div>
                  </form>
                </div>
              </DialogContent>
            </Dialog>

            {/* View Billing Details Dialog */}
            <Dialog open={viewDialog} onOpenChange={setViewDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Billing Details</DialogTitle>
                  {/* <apre>{JSON.stringify(billingDetails)}</apre> */}
                </DialogHeader>
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  billingDetails && (
                    <div className="space-y-6">
                      {/* Client Info */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">
                          {billingDetails.client.client_name}
                        </h3>
                        <p className="text-sm text-slate-600">
                          Account: {billingDetails.client.account_number}
                        </p>
                        <p className="text-sm text-slate-600">
                          Plan: {billingDetails.client.plan_name} - ₱
                          {billingDetails.client.monthly_fee}/mo
                        </p>
                        {billingDetails.client.status === "New" &&
                          billingDetails.client.installation_date && (
                            <p className="text-xs text-slate-500">
                              Installed:{" "}
                              {formatDate(
                                billingDetails.client.installation_date,
                              )}
                            </p>
                          )}
                      </div>

                      {/* Balance Summary */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* <pre>{JSON.stringify(billingDetails)} </pre> */}
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-blue-600">
                            Current Charges
                          </p>
                          <p className="text-2xl font-bold text-blue-700">
                            ₱
                            {(billingDetails.client.status !== "Paid"
                              ? billingDetails.current_charges?.toLocaleString()
                              : 0
                            ).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-amber-600">
                            Previous Balance
                          </p>
                          <p className="text-2xl font-bold text-amber-700">
                            {/* ₱{billingDetails.previous_balance?.toLocaleString()} */}
                            ₱
                            {(billingDetails.client.status !== "Paid"
                              ? billingDetails.previous_balance?.toLocaleString()
                              : 0
                            ).toLocaleString()}
                          </p>
                        </div>
                        <div
                          className={`rounded-lg p-4 text-center ${billingDetails.outstanding_balance > 0 ? "bg-red-50" : "bg-emerald-50"}`}
                        >
                          <p
                            className={`text-sm ${billingDetails.outstanding_balance > 0 ? "text-red-600" : "text-emerald-600"}`}
                          >
                            Outstanding Balance
                          </p>
                          <p
                            className={`text-2xl font-bold ${billingDetails.outstanding_balance > 0 ? "text-red-700" : "text-emerald-700"}`}
                          >
                            ₱
                            {billingDetails.outstanding_balance?.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Due Date & Disconnection */}
                      <div className="flex gap-4">
                        <div className="flex-1 border border-slate-200 rounded-lg p-4">
                          <p className="text-sm text-slate-500">Due Date</p>
                          <p className="font-semibold text-slate-900">
                            {formatDate(billingDetails.current_due_date)}
                          </p>
                        </div>
                        {billingDetails.disconnection_date && (
                          <div className="flex-1 border border-red-200 bg-red-50 rounded-lg p-4">
                            <p className="text-sm text-red-600">
                              Disconnection Date
                            </p>
                            <p className="font-semibold text-red-700">
                              {formatDate(billingDetails.disconnection_date)}
                            </p>
                          </div>
                        )}
                        {billingDetails.is_disconnected && (
                          <div className="flex-1 border border-red-200 bg-red-50 rounded-lg p-4">
                            <p className="text-sm text-red-600">Status</p>
                            <p className="font-semibold text-red-700 flex items-center gap-2">
                              <WifiOff className="w-4 h-4" /> Disconnected
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Payment History */}
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">
                          Recent Payments
                        </h4>
                        {billingDetails.payment_history?.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-4">
                            No payment history
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {billingDetails.payment_history?.map((payment) => (
                              <div
                                key={payment.id}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-slate-900">
                                    ₱{payment.amount?.toLocaleString()}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {payment.payment_method} •{" "}
                                    {payment.reference_number || "N/A"}
                                  </p>
                                </div>
                                <p className="text-sm text-slate-500">
                                  {formatDate(payment.created_at)}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col gap-4">
          <div div className="flex items-center gap-4 w-full">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              {loading && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
              )}
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
              />
            </div>
            {/* Refresh Button */}
            <Button
              onClick={handleRefresh}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-600/30"
            >
              Refresh
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
          {/* <div className="flex gap-2 sm:gap-3 flex-wrap">
            <Select
              value={accountStatusFilter}
              onValueChange={setAccountStatusFilter}
            >
              <SelectTrigger className="w-[220px] rounded-lg bg-white text-sm">
                <Filter className="w-4 h-4 mr-1" />
                <SelectValue placeholder="Account Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Account Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <Filter className="w-4 h-4 mr-1" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Disconnected">Disconnected</SelectItem>
                <SelectItem value="Extended">Extended</SelectItem>
                <SelectItem value="Free">Free</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="NotFree">Not Free</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={balanceFilter} onValueChange={setBalanceFilter}>
              <SelectTrigger className="w-[140px] rounded-lg bg-white text-sm">
                <SelectValue placeholder="Balance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Balances</SelectItem>
                <SelectItem value="with_balance">With Balance</SelectItem>
                <SelectItem value="zero_balance">Zero Balance</SelectItem>
                <SelectItem value="fully_paid">Fully Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serverFilter} onValueChange={setServerFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <SelectValue placeholder="Server" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Servers</SelectItem>
                {servers.map((server) => (
                  <SelectItem key={server.id} value={server.id}>
                    {server.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-220px)]">
          {/* TABLE SCROLL AREA */}
          <div className="overflow-auto flex-1">
            <table className="w-full" data-testid="clients-table">
              <thead className="border-b border-slate-200 sticky top-0 bg-blue-100 z-10">
                <tr className="border-b">
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    SOA No.
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Account No.
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Fullname
                  </th>
                  <th className="px-6 sm:px-28 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Date Details
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Previous Balance
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Current Balance
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Total Payment
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Total Amount Due
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-10 text-center text-slate-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : clients?.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      No clients found
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr
                      key={client.id}
                      data-testid={`client-row-${client.id}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm font-mono text-slate-500 ">
                          {client.soa_number}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3">
                        <p className="text-xs font-mono text-slate-500 sm:text-sm sm:text-slate-900">
                          {client.account_number}
                        </p>
                        <p
                          onClick={() => openEditDialog(client)}
                          className="text-sm font-semibold text-slate-900 cursor-pointer sm:hidden"
                        >
                          {client.client_name}
                        </p>
                        <p className="text-xs font-mono text-slate-500 sm:hidden">
                          {client.email}
                        </p>
                        <p className="text-xs font-mono text-slate-500 sm:hidden">
                          {client.contact_number}
                        </p>
                        <p className="text-xs font-mono text-slate-500 sm:hidden">
                          {client.address}
                        </p>
                        <p className="text-xs font-mono text-slate-500 sm:hidden">
                          {client.plan_name}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm font-semibold text-slate-900">
                          {client.client_name}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="flex flex-row text-sm text-slate-900">
                          Billing Start Period : {client.billing_period_start}
                        </p>
                        <p className="flex flex-row text-sm text-slate-900">
                          Billing End Period : {client.billing_period_end}
                        </p>
                        <p className="flex flex-row text-sm text-slate-900">
                          Due Date : {client.due_date}
                        </p>
                        <p className="flex flex-row text-sm text-red-600 font-bold">
                          Disconnection Date : {client.disconnection_date}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm font-mono text-slate-500 ">
                          {client.previous_balance}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm font-mono text-slate-500 ">
                          {client.current_charges}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm text-slate-500">
                          {client.total_payments}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 hidden sm:table-cell md:table-cell">
                        <p className="text-sm font-mono text-slate-500 ">
                          {client.total_amount_due}
                        </p>
                      </td>

                      <td className="px-3 sm:px-6 py-2 text-right">
                        <div className="flex gap-1 justify-end items-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              downloadSOAPDF(client.id, client.soa_number)
                            }
                            className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100 mt-4"
                            data-testid={`download-soa-${client.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download PDF
                          </Button>
                          {/* <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(client)}
                            className="hover:bg-blue-50 hover:text-blue-600 rounded-lg mt-4"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Edit className="w-2 h-2" />
                          </Button> */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDialog(client, "delete")}
                            className="hover:bg-red-50 hover:text-red-600 rounded-lg mt-4"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-2 h-2" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* STICKY PAGINATION */}
          {totalPages > 1 && (
            <div className="border-t bg-white px-4 py-3 items-center justify-between sticky bottom-0 hidden lg:flex">
              <p className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  (currentPage - 1) * itemsPerPage + clients?.length,
                )}{" "}
                of {totalClients}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(currentPage - 1, searchQuery)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(currentPage + 1, searchQuery)}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="p-8 block lg:hidden">
          <span> </span>
        </div>
        {/* Results count */}
        <div className="mt-4 text-sm text-slate-600 hidden lg:block">
          {/* Showing {filteredClients?.length} of {totalClients?.length} clients */}
          Showing {clients.length} of {totalClients} clients
        </div>

        <div className="fixed bottom-0 left-0 w-full bg-white border-t shadow-md z-40 block lg:hidden">
          {/* <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between"> */}
          {totalPages > 1 && (
            <div className="border-t bg-white px-4 py-3 flex items-center justify-between sticky bottom-0">
              <p className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  (currentPage - 1) * itemsPerPage + clients?.length,
                )}{" "}
                of {totalClients}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => fetchData(currentPage - 1, searchQuery)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => fetchData(currentPage + 1, searchQuery)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {/* </div> */}
        </div>

        {dialog.show && (
          <ConfirmDialog
            title={
              dialog.action === "reconnect"
                ? "Reconnect Client"
                : dialog.action === "delete"
                  ? "Delete Client SOA"
                  : dialog.action === "generate soa"
                    ? "Generate SOA"
                    : "Confirm Action"
            }
            message={
              dialog.action === "generate soa"
                ? `Are you sure you want to generate SOA for ${dialog.client.client_name}?`
                : dialog.client
                  ? `Are you sure you want to ${dialog.action} ${dialog.client.client_name}?`
                  : ""
            }
            confirmText={
              dialog.action.charAt(0).toUpperCase() + dialog.action.slice(1)
            }
            icon={
              dialog.action === "generate soa"
                ? FileText
                : dialog.action === "reconnect"
                  ? WifiSync
                  : dialog.action === "delete"
                    ? Trash2
                    : CheckCircle
            }
            color={dialog.action === "delete" ? "red" : "blue"}
            onConfirm={handleConfirm}
            onCancel={() =>
              setDialog({ show: false, action: "", client: null })
            }
          />
        )}

        {/* Reconnect Dialog */}
        {/* <Dialog open={reconnectDialog} onOpenChange={setReconnectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{reconnectData.title}</DialogTitle>
            </DialogHeader>
            <form className="space-y-4">
              <div>
                <babel>Message</babel>
                <p className="text-sm text-slate-600 mt-3">
                  {reconnectData.message}
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg"
                data-testid="client-submit-btn"
              >
                Reconnect Client
              </Button>
            </form>
          </DialogContent>
        </Dialog> */}
      </div>
    </AdminLayout>
  );
}
